/**
 * SelectField — un desplegable con su etiqueta y su error.
 *
 * Es un `<select>` NATIVO a propósito: en un teléfono abre la rueda del sistema
 * operativo, que se maneja mucho mejor que cualquier lista dibujada a mano, y
 * no arrastra ninguna dependencia.
 *
 * 🟢 **Y acá el repo ya tenía ganada la batalla que el sistema declara
 * pendiente.** Su `PENDING.md` anota el `color-scheme` de los controles
 * nativos como «el defecto más visible del modo oscuro hoy»: la lista de
 * `<option>` la dibuja el sistema operativo y hereda el fondo del control, así
 * que en oscuro quedaba texto claro sobre fondo claro. `index.css` ya declara
 * `color-scheme: light` en `html` y `dark` en `html.dark`, de modo que este
 * port lo hereda sin tocar nada — el mismo hallazgo que documenta
 * `DatePicker.tsx`.
 *
 * ⚠️ La flecha va con `pointerEvents: 'none'`: dibujada encima del control,
 * sin ella el clic sobre la flecha no abriría el desplegable.
 */
import { useId } from 'react'
import type {
  ComponentPropsWithRef, CSSProperties, HTMLAttributes, ReactNode, SelectHTMLAttributes,
} from 'react'

export interface SelectOption {
  value: string
  label: string
}

export interface SelectFieldProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'className' | 'style' | 'onChange'> {
  label?: ReactNode
  value?: string
  onChange?: SelectHTMLAttributes<HTMLSelectElement>['onChange']
  options?: SelectOption[]
  /**
   * Las opciones escritas a mano, cuando no son una lista plana de
   * `{value, label}`.
   *
   * ⚠️ Hace falta porque `options` no puede expresar un `<optgroup>`, y el
   * producto los usa: los tipos de documento de referencia del DTE van
   * agrupados por familia, y aplanarlos perdería esa agrupación. Con `children`
   * la pantalla escribe sus `<option>` como siempre y el componente sigue
   * aportando la etiqueta asociada y el error anunciado, que es a lo que se
   * migra.
   */
  children?: ReactNode
  /**
   * Lo que va al `<select>` y no al contenedor.
   *
   * 🔴 Es por donde entra `react-hook-form`: `selectProps={register('comuna')}`.
   * Sin admitir su `ref`, el desplegable se vería igual y el formulario no lo
   * leería.
   */
  selectProps?: Omit<ComponentPropsWithRef<'select'>, 'id'>
  /** Control más bajo, igual que en . */
  dense?: boolean
  /** La opción vacía de arriba. Sin ella el primer valor queda elegido de hecho. */
  placeholder?: string
  error?: ReactNode
  hint?: ReactNode
  disabled?: boolean
  required?: boolean
  id?: string
  name?: string
  className?: string
  style?: CSSProperties
}

export function SelectField({
  label,
  value,
  onChange,
  options = [],
  children,
  selectProps,
  placeholder,
  error,
  hint,
  disabled = false,
  required = false,
  dense = false,
  id: idProp,
  name,
  className = '',
  style,
  ...rest
}: SelectFieldProps) {
  const auto = useId()
  const id = idProp || `cl-${auto}`
  const idError = `${id}-err`
  const idAyuda = `${id}-hint`

  return (
    <div className={`cl-field ${dense ? 'cl-field--dense' : ''} ${className}`.trim()} style={style} {...rest}>
      {label && (
        <label className="cl-label" htmlFor={id}>
          {label}
          {required && (
            <span aria-hidden="true" style={{ color: 'rgb(var(--color-danger))' }}>
              {' *'}
            </span>
          )}
        </label>
      )}
      <div style={{ position: 'relative' }}>
        <select
          id={id}
          name={name}
          className={dense ? 'cl-input cl-input--sm' : 'cl-input'}
          value={value}
          onChange={onChange}
          disabled={disabled}
          // `aria-required` y no el nativo, por lo mismo que en `TextField`:
          // el `required` del navegador frena el envío con su propio mensaje
          // antes de que react-hook-form muestre el de la pantalla.
          aria-required={required || undefined}
          aria-invalid={error ? true : undefined}
          aria-describedby={
            [error ? idError : null, hint ? idAyuda : null].filter(Boolean).join(' ') ||
            undefined
          }
          // El hueco de la derecha es para la flecha propia; sin `appearance`
          // el navegador dibuja además la suya y quedan dos.
          style={{ appearance: 'none', paddingRight: 34 }}
          {...selectProps}
        >
          {placeholder && <option value="">{placeholder}</option>}
          {options.map(o => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
          {children}
        </select>
        <svg
          aria-hidden="true"
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          style={{
            position: 'absolute',
            right: 12,
            top: '50%',
            transform: 'translateY(-50%)',
            color: 'rgb(var(--color-content-subtle))',
            pointerEvents: 'none',
          }}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>
      {error && (
        <div className="cl-error" id={idError} role="alert">
          {error}
        </div>
      )}
      {hint && !error && (
        <div className="cl-hint" id={idAyuda}>
          {hint}
        </div>
      )}
    </div>
  )
}
